import React, { useState } from 'react';
import axios from 'axios';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { REACT_APP_API_BASE_URL } from '../../apiurl';
import DataTable from '../../components/common/DataTable/DataTable';
import Select from "react-select";
import { usePermission } from '../../hooks/usePermissions';
import styles from './InternalTokenList.module.css';

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const GET_INTERNAL_TOKENS = gql`
  query GetInternalTokens($page: Int!, $limit: Int!, $search: String, $sortBy: String, $sortOrder: String, $exch_seg: String, $instrumenttype: String, $websocket_enabled: Boolean, $tradeable: Boolean, $displaytab: [String!], $fromDate: String, $toDate: String) {
    getInternalTokens(page: $page, limit: $limit, search: $search, sortBy: $sortBy, sortOrder: $sortOrder, exch_seg: $exch_seg, instrumenttype: $instrumenttype, websocket_enabled: $websocket_enabled, tradeable: $tradeable, displaytab: $displaytab, fromDate: $fromDate, toDate: $toDate) {
      items {
        id
        token
        symbol
        name
        exch_seg
        instrumenttype
        websocket_enabled
        websocket_slot
        tradeable
        displaytab
        created_at
        updated_at
      }
      totalCount
    }
  }
`;

const GET_EXCHANGES = gql`
  query GetExchanges {
    getExchanges {
      exchange_code
      exchange_name
    }
    getAllIndicesTypes {
        unique_id
        indice_name
    }
  }
`;

const GET_SCRIPT_TYPES = gql`
  query GetScriptTypes {
    getScriptTypes {
      type_code
      type_name
    }
  }
`;

const InternalTokenList = () => {
    // API Base URL (Still needed for mutations)
    const API_URL = `${REACT_APP_API_BASE_URL}api/internaltoken`;

    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState(''); // Stores the value specifically for the query triggering
    const [sortBy, setSortBy] = useState('symbol');
    const [sortOrder, setSortOrder] = useState('ASC');
    const [filterExchSeg, setFilterExchSeg] = useState('');
    const [filterInstrumentType, setFilterInstrumentType] = useState('');
    const [filterWebsocket, setFilterWebsocket] = useState('');
    const [filterTradeable, setFilterTradeable] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const LIMIT = 10;

    const {hasPermission} = usePermission()

    const { loading, error, data, refetch } = useQuery(GET_INTERNAL_TOKENS, {
        variables: {
            page,
            limit: LIMIT,
            search: searchQuery,
            sortBy,
            sortOrder,
            exch_seg: filterExchSeg || undefined,
            instrumenttype: filterInstrumentType || undefined,
            websocket_enabled: filterWebsocket === '' ? undefined : filterWebsocket === 'true',
            tradeable: filterTradeable === '' ? undefined : filterTradeable === 'true',
            fromDate: fromDate || undefined,
            toDate: toDate || undefined
        },
        fetchPolicy: 'network-only'
    });

    const { data: exchangeData } = useQuery(GET_EXCHANGES);
    const { data: scriptTypeData } = useQuery(GET_SCRIPT_TYPES);

    // Derived state
    const tokens = data?.getInternalTokens?.items || [];
    const totalCount = data?.getInternalTokens?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / LIMIT);

    const handleNext = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const handlePrevious = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleSearch = () => {
        setSearchQuery(searchInput);
        setPage(1); // Reset to first page on new search
    };

    const handleSort = (columnKey) => {
        if (sortBy === columnKey) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(columnKey);
            setSortOrder('ASC');
        }
        setPage(1);
    };

    // Local state for mutations
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        token: '',
        symbol: '',
        name: '',
        exch_seg: 'NSE',
        instrumenttype: '',
        websocket_enabled: false,
        websocket_slot: 1,
        tradeable: true,
        created_at: '',
        updated_at: '',
        displaytab: []
    });
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState('')

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editMode) {
                await axios.put(`${API_URL}/update/${editId}`, formData);
                alert('Token updated successfully');
            } else {
                await axios.post(`${API_URL}/add`, formData);
                alert('Token added successfully');
            }
            setShowForm(false);
            setFormData({
                token: '',
                symbol: '',
                name: '',
                exch_seg: 'NSE',
                instrumenttype: '',
                websocket_enabled: false,
                websocket_slot: 1,
                tradeable: true,
                displaytab: []
            });
            setEditMode(false);
            refetch();
        } catch (error) {
            console.error('Error saving token:', error);
            alert('Failed to save token');
        }
    };

    const handleEdit = (token) => {
        if (token) {
            setEditId(Number(token.id))
        }
        setFormData(token);
        setEditMode(true);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this token?')) {
            try {
                await axios.delete(`${API_URL}/delete/${id}`);
                alert('Token deleted successfully');
                refetch();
            } catch (error) {
                console.error('Error deleting token:', error);
                alert('Failed to delete token');
            }
        }
    };

    const columns = [
        { key: 'id', label: 'ID', sortable: true, render: (val) => val },
        {
            key: 'displaytab', label: 'Display Tab', render: (val) => (
                <span>{Array.isArray(val) ? val.join(", ") : ''}</span>
            )
        },
        { key: 'token', label: 'Token', sortable: true },
        { key: 'symbol', label: 'Symbol', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'exch_seg', label: 'Exchange', sortable: true },
        { key: 'instrumenttype', label: 'Instrument', sortable: true },
        {
            key: 'websocket_enabled',
            label: 'WS',
            sortable: true,
            render: (val) => val ? 'Yes' : 'No'
        },
        {
            key: 'websocket_slot',
            label: 'Slot',
            sortable: true,
            render: (val) => `${val || 1}`
        },
        {
            key: 'tradeable',
            label: 'Tradable',
            sortable: true,
            render: (val) => val ? 'Yes' : 'No'
        },
        {
            key: 'created_at',
            label: 'Created',
            sortable: true,
            render: (val) => new Date(Number(val) || val).toLocaleString()
        },
        {
            key: 'updated_at',
            label: 'Updated',
            sortable: true,
            render: (val) => new Date(Number(val) || val).toLocaleString()
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className={styles.flexCenterGap8}>
                    {hasPermission("MODIFY_INTERNALTOKEN_INDICESCONTROL") && (
                        <button className={styles.iconBtn} onClick={() => handleEdit(row)} title="Edit Token">
                            <EditIcon />
                        </button>
                    )}
                    {hasPermission("DELETE_INTERNALTOKEN_INDICESCONTROL") && (
                        <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(row.id)} title="Delete Token">
                            <TrashIcon />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <h2>Internal Token List</h2>
                    <p>Manage application tokens and configurations</p>
                </div>
                
                <div className={styles.headerRight}>
                    <div className={styles.searchBox}>
                        <span className={styles.searchIcon}>
                            <SearchIcon />
                        </span>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search tokens..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </div>
                    {hasPermission("CREATE_INTERNALTOKEN_INDICESCONTROL") && (
                        <button
                            className={styles.primaryBtn}
                            onClick={() => { setShowForm(true); setEditMode(false); setFormData({ token: '', symbol: '', name: '', exch_seg: 'NSE', instrumenttype: '', websocket_enabled: false, tradeable: true }); }}
                        >
                            + Add Token
                        </button>
                    )}
                </div>
            </div>

            {/* Advanced Filters */}
            <div className={styles.filtersWrapper}>
                <span className={styles.filtersLabel}>Filters:</span>

                <div className={styles.filterGroup}>
                    <label className={styles.filterInputLabel}>From:</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterInputLabel}>To:</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterInputLabel}>Exch:</label>
                    <select
                        value={filterExchSeg}
                        onChange={(e) => { setFilterExchSeg(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        {exchangeData && exchangeData.getExchanges.map((ex) => (
                            <option key={ex.exchange_code} value={ex.exchange_code}>{ex.exchange_code}</option>
                        ))}
                    </select>
                </div>


                <div className={styles.filterGroup}>
                    <label className={styles.filterInputLabel}>Inst Type:</label>
                    <select
                        value={filterInstrumentType}
                        onChange={(e) => { setFilterInstrumentType(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        {scriptTypeData && scriptTypeData.getScriptTypes.map((st) => (
                            <option key={st.type_code} value={st.type_code}>
                                {st.type_code}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterInputLabel}>WS:</label>
                    <select
                        value={filterWebsocket}
                        onChange={(e) => { setFilterWebsocket(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterInputLabel}>Tradable:</label>
                    <select
                        value={filterTradeable}
                        onChange={(e) => { setFilterTradeable(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>

                <button
                    onClick={() => { setFilterExchSeg(''); setFilterInstrumentType(''); setFilterWebsocket(''); setFilterTradeable(''); setFromDate(''); setToDate(''); setSearchInput(''); setSearchQuery(''); setPage(1); }}
                    className={styles.clearFiltersBtn}
                >
                    Clear
                </button>
            </div>

            {showForm && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>{editMode ? 'Edit Token' : 'Add New Token'}</h3>
                            <button onClick={() => setShowForm(false)} className={styles.modalCloseBtn}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit} className={styles.formGrid}>
                            <div>
                                <label className={styles.formLabel}>Token:</label>
                                <input type="text" name="token" value={formData.token} onChange={handleInputChange} disabled={editMode} className={styles.formInput} />
                            </div>
                            <div>
                                <label className={styles.formLabel}>Symbol:</label>
                                <input type="text" name="symbol" value={formData.symbol} onChange={handleInputChange} disabled={editMode} className={styles.formInput} />
                            </div>
                            <div>
                                <label className={styles.formLabel}>Name:</label>
                                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className={styles.formInput} />
                            </div>

                            <div>
                                <label className={styles.formLabel}>Display Tab</label>
                                <Select
                                    isMulti
                                    options={
                                        exchangeData
                                            ? exchangeData.getAllIndicesTypes.map(t => ({
                                                value: t.indice_name.toUpperCase(),
                                                label: t.indice_name.toUpperCase(),
                                            }))
                                            : []
                                    }
                                    value={
                                        exchangeData
                                            ? exchangeData.getAllIndicesTypes
                                                .filter(t => (formData.displaytab || []).includes(t.indice_name.toUpperCase()))
                                                .map(t => ({
                                                    value: t.indice_name.toUpperCase(),
                                                    label: t.indice_name.toUpperCase(),
                                                }))
                                            : []
                                    }

                                    onChange={(selected) =>
                                        setFormData(prev => ({
                                            ...prev,
                                            displaytab: selected.map(s => s.value.toUpperCase()),
                                        }))
                                    }
                                />

                            </div>

                            <div>
                                <label className={styles.formLabel}>Exchange Segment:</label>
                                <select name="exch_seg" value={formData.exch_seg} onChange={handleInputChange} className={styles.formInput}>
                                    {/* <option value="NSE">NSE</option>
                                <option value="BSE">BSE</option> */}
                                    {exchangeData && exchangeData.getExchanges.map((ex) => (
                                        <option key={ex.exchange_code} value={ex.exchange_code}>
                                            {ex.exchange_code} - {ex.exchange_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={styles.formLabel}>Instrument Type:</label>
                                <select name="instrumenttype" value={formData.instrumenttype} onChange={handleInputChange} className={styles.formInput}>
                                    <option value="">Select Instrument Type</option>
                                    {scriptTypeData && scriptTypeData.getScriptTypes.map((st) => (
                                        <option key={st.type_code} value={st.type_code}>
                                            {st.type_code} - {st.type_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={styles.formLabel}>WebSocket Slot</label>
                                <select
                                    name="websocket_slot"
                                    value={formData.websocket_slot || 1}
                                    onChange={(e) => setFormData(prev => ({ ...prev, websocket_slot: Number(e.target.value) }))}
                                    className={styles.formInput}
                                >
                                    <option value={1}>ws1 (default)</option>
                                    <option value={2}>ws2</option>
                                    <option value={3}>ws3</option>
                                </select>
                            </div>
                            <div className={styles.checkboxGroup}>
                                <input type="checkbox" name="websocket_enabled" checked={formData.websocket_enabled} onChange={handleInputChange} className={styles.checkboxInput} />
                                <label>Web Socket Enabled</label>
                            </div>
                            <div className={styles.checkboxGroup}>
                                <input type="checkbox" name="tradeable" checked={formData.tradeable} onChange={handleInputChange} className={styles.checkboxInput} />
                                <label>Tradeable</label>
                            </div>
                            {editMode && (
                                <>
                                    <div className={styles.formGridSpan2}>
                                        <div>
                                            <label className={styles.formLabel}>Created At:</label>
                                            <input type="text" value={formData.created_at ? new Date(Number(formData.created_at) || formData.created_at).toLocaleString() : ''} disabled className={styles.formInputDisabled} />
                                        </div>
                                        <div>
                                            <label className={styles.formLabel}>Updated At:</label>
                                            <input type="text" value={formData.updated_at ? new Date(Number(formData.updated_at) || formData.updated_at).toLocaleString() : ''} disabled className={styles.formInputDisabled} />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className={styles.formActions}>
                                <button type="button" onClick={() => setShowForm(false)} className={styles.btnCancel}>Cancel</button>
                                <button type="submit" className={styles.btnSave}>Save Token</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {loading ? (
                <div className={styles.loadingText}>Loading...</div>
            ) : error ? (
                <div className={styles.errorText}>Error: {error.message}</div>
            ) : (
                <DataTable
                    id="id"
                    title=""
                    columns={columns}
                    data={tokens}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            )}

            <div className={styles.imTableFooter}>
                <span>Showing {tokens.length} records (Total: {totalCount})</span>

                {totalPages > 1 && (
                    <div className={styles.imPagination}>
                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page === 1}
                            onClick={handlePrevious}
                        >
                            {"<"}
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (
                                p === 1 ||
                                p === totalPages ||
                                (p >= page - 1 && p <= page + 1)
                            ) {
                                return (
                                    <button
                                        type="button"
                                        key={p}
                                        className={`${styles.imPageBtn} ${page === p ? styles.active : ""}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }

                            if (p === page - 2 || p === page + 2) {
                                return (
                                    <span key={p} className={styles.paginationDots}>
                                        ...
                                    </span>
                                );
                            }

                            return null;
                        })}

                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page >= totalPages || loading}
                            onClick={handleNext}
                        >
                            {">"}
                        </button>
                    </div>
                )}
                
                <span className={styles.pageCountInfo}>
                    Page {page} of {totalPages || 1}
                </span>
            </div>
        </div>
    );
};

export default InternalTokenList;
